import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import api from "../api";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import PageTitle from "../components/PageTitleCompoent";
import GroomingCard from "../components/GroomingCard";
import DeleteWithConfirmation from "../components/DeleteWithConfirmation";

import "../css/GroomingScreen.css";

export default function GroomingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasAddedRef = useRef(false);
  const containerRef = useRef(null);
 const isMounted = useRef(true);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [editingPackageId, setEditingPackageId] = useState(null);

  // ✅ Fetch packages
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await api.get("/grooming-packages/");
        console.log("Packages API Response:", res.data);

        const mapped = res.data.map((item) => ({
          id: item.id,
          title: item.package_name,
          subscribedPrice: parseFloat(item.subscribed_user_offer_amount),
          unsubscribedPrice: parseFloat(item.package_amount),
          services: item.services
            ? item.services.split(",").map((s) => s.trim())
            : [],
          tag: item.tags || "",
          duration: item.duration,
          isPopular: item.tags?.toLowerCase() === "popular",
        }));
 if (isMounted.current) setPackages(mapped);
        setPackages(mapped);
      } catch (err) {
        console.error("Failed to fetch packages:", err);
        setError("Failed to load packages.");
      } finally {
        setLoading(false);
         if (isMounted.current) setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  // ✅ Animate in cards with GSAP
  useEffect(() => {
    if (packages.length > 0 && containerRef.current) {
      gsap.fromTo(
        ".grooming-card-animate",
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.15,
        }
      );
    }
  }, [packages]);

  // ✅ Handle added package
  useEffect(() => {
    if (location.state?.newPackage && !hasAddedRef.current) {
      setPackages((prev) => [...prev, location.state.newPackage]);
      hasAddedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // ✅ Delete modal handlers
  const openDeleteModal = (pkg) => {
    setPackageToDelete(pkg);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setPackageToDelete(null);
  };

  const confirmDelete = async () => {
    if (!packageToDelete) return;
    try {
      await api.delete(`/grooming-packages/${packageToDelete.id}/`);
      setPackages((prev) => prev.filter((p) => p.id !== packageToDelete.id));
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete package:", err);
      alert("Failed to delete package. Please try again.");
    }
  };

  // ✅ Edit handlers
  const handleEdit = (pkg) => setEditingPackageId(pkg.id);

  const handleSave = async (updatedPackage) => {
    try {
      const payload = {
        services: updatedPackage.services.join(", "),
        subscribed_user_offer_amount: Number(updatedPackage.subscribed_user_offer_amount),
        package_amount: Number(updatedPackage.package_amount),
      };

      const res = await api.patch(
        `/grooming-packages/${updatedPackage.id}/`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const saved = {
        id: res.data.id,
        title: res.data.package_name,
        subscribedPrice: parseFloat(res.data.subscribed_user_offer_amount),
        unsubscribedPrice: parseFloat(res.data.package_amount),
        services: res.data.services
          ? res.data.services.split(",").map((s) => s.trim())
          : [],
        tag: res.data.tags || "",
        duration: res.data.duration,
        isPopular: res.data.tags?.toLowerCase() === "popular",
      };

      setPackages((prev) =>
        prev.map((p) => (p.id === saved.id ? saved : p))
      );
      setEditingPackageId(null);
    } catch (err) {
      console.error("Failed to save package:", err.response || err);
      alert("Failed to save package. Please try again.");
    }
  };

  const handleCloseEdit = () => setEditingPackageId(null);
  const handleBack = () => navigate(-1);

  // ✅ Card motion variants
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
    hover: { scale: 1.05, transition: { type: "spring", stiffness: 300 } },
  };

  return (
    <div className="container">
      <Sidebar />
      <div className="main">
        <Header />
        <motion.div
          className="main-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="heading-sec">
            <PageTitle title="Grooming" onBack={handleBack} />
            <div className="add-package-sec">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="add-package-btn"
                onClick={() =>
                  navigate("/add_package", { state: { type: "Grooming" } })
                }
              >
                <IoMdAdd className="add-package-icon" color="#EBA8BF" /> Add Package
              </motion.button>
            </div>
          </div>

          {loading && <p>Loading packages...</p>}
          {error && <p style={{ color: "red" }}>{error}</p>}

          <div className="grooming-cards-container" ref={containerRef}>
            <AnimatePresence>
              {packages.map((pkg) => (
                <motion.div
                  key={pkg.id}
                  className="grooming-card-animate"
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                 
                  exit={{ opacity: 0, y: 20, transition: { duration: 0.3 } }}
                >
                  <GroomingCard
                    id={pkg.id}
                    title={pkg.title}
                    subscribedPrice={pkg.subscribedPrice}
                    unsubscribedPrice={pkg.unsubscribedPrice}
                    services={pkg.services}
                    tag={pkg.tag}
                    isEditable={editingPackageId === pkg.id}
                    onEdit={() => handleEdit(pkg)}
                    onDelete={() => openDeleteModal(pkg)}
                    onSave={handleSave}
                    onClose={handleCloseEdit}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <DeleteWithConfirmation
            isOpen={isDeleteModalOpen}
            onCancel={closeDeleteModal}
            onConfirm={confirmDelete}
            title={`Delete ${packageToDelete?.title || "Package"}?`}
            message="Are you sure you want to delete this package? This action cannot be undone."
          />
        </motion.div>
        
      </div>
    </div>
  );
}
